interface ShortUrlConfig {
  segment: string;
  path: string;
  param: string;
  extraParams?: { [key: string]: string };
}

const SHORT_URL_CONFIGS: ShortUrlConfig[] = [
  { segment: 'pr', path: 'order-extra-payment', param: 'token' },
  { segment: 'd', path: 'order-details', param: 'sid' },
  { segment: 'r', path: 'order-details', param: 'sid', extraParams: { t: 'CUSTOMER' } }
];

// Matches the short link at the start of the path only, so a deeper url that merely contains
// '/d/' somewhere is left alone.
const SHORT_URL_PATTERN = new RegExp(`^/(${SHORT_URL_CONFIGS.map((config) => config.segment).join('|')})/([^/]+)`);

// Lets one code path handle both the absolute href the browser has and the path-only url express sees.
const RELATIVE_URL_ORIGIN = 'http://short-url.local';

function normalizeBaseHref(baseHref: string): string {
  const normalized = `/${baseHref || ''}`.replace(/\/+/g, '/').replace(/\/$/, '');

  return normalized === '/' ? '' : normalized;
}

/**
 * Expands the short links sent out in SMS and email ('/d/<sid>', '/r/<sid>', '/pr/<token>') into the
 * routes the apps actually serve. Returns null when the url is not a short link.
 *
 * The value is read from the path and the rest of the query string is preserved, so any additional
 * query params stay separate params instead of being swallowed into the sid.
 */
export function expandShortUrl(url: string, baseHref = '/'): string | null {
  if (!url) {
    return null;
  }

  const isAbsolute = /^[a-z][a-z\d+.-]*:\/\//i.test(url);
  const parsed = new URL(url, RELATIVE_URL_ORIGIN);
  const prefix = normalizeBaseHref(baseHref);

  // In MARS mode nginx strips the prefix before express sees the url but the browser href still
  // carries it, so drop it before matching and add it back once — otherwise it ends up doubled.
  const path = prefix && parsed.pathname.startsWith(`${prefix}/`)
    ? parsed.pathname.slice(prefix.length)
    : parsed.pathname;

  const match = path.match(SHORT_URL_PATTERN);

  if (!match) {
    return null;
  }

  const config = SHORT_URL_CONFIGS.find((item) => item.segment === match[1]);

  parsed.pathname = `${prefix}/${config.path}`;
  parsed.searchParams.set(config.param, decodeURIComponent(match[2]));

  Object.keys(config.extraParams || {}).forEach((key) => parsed.searchParams.set(key, config.extraParams[key]));

  return isAbsolute ? parsed.href : `${parsed.pathname}${parsed.search}${parsed.hash}`;
}
